import { FlattenedNode, Node } from "./types";
import { File } from "@planzone/dto";
import JSZip from "jszip";
import mimeDb from "mime-db";

function mapExtToMime(mime: string, extensions: readonly string[] = []) {
  return extensions.reduce((map, ext) => ({ [ext]: mime }), {});
}

function getExtMap() {
  return Object.entries(mimeDb).reduce<{ [key: string]: string }>(
    (map, [mime, { extensions }]) => ({
      ...map,
      ...mapExtToMime(mime, extensions),
    }),
    {},
  );
}

function getMimeTypeFromExt(extMap: { [key: string]: string }, ext: string) {
  return extMap[ext] || "application/octet-stream";
}

function fileNameToMimeType(
  extMap: { [key: string]: string },
  fileName: string,
) {
  const splitName = fileName.split(".");
  const fileExtension = (splitName.pop() || "").toLowerCase();

  return !!fileExtension
    ? getMimeTypeFromExt(extMap, fileExtension)
    : "application/octet-stream";
}

function buildTree(nodes: FlattenedNode[]): Node[] {
  const nodeMap = new Map<string, Node>();
  const rootNode: Node[] = [];

  nodes.forEach((flattenedNode) => {
    let currentPath = "";
    let currentChildren = rootNode;

    flattenedNode.path.forEach((pathItem) => {
      currentPath = currentPath ? `${currentPath}.${pathItem}` : pathItem;
      const existingNode = nodeMap.get(currentPath);

      if (existingNode) {
        if (existingNode.type === "folder") {
          currentChildren = existingNode.children;
        }
      } else {
        const newNode: Node = {
          type: "folder",
          name: pathItem,
          children: [],
          mimeType: "",
        };

        currentChildren.push(newNode);
        nodeMap.set(currentPath, newNode);
        currentChildren = newNode.children;
      }
    });

    if (flattenedNode.type === "document") {
      currentChildren.push({
        type: "document",
        name: flattenedNode.name,
        children: [],
        mimeType: flattenedNode.mimeType,
      });
    }
  });

  return rootNode;
}

function extractZip(file: File) {
  const zip = new JSZip();
  const extMap = getExtMap();

  return fetch(file.uri)
    .then((res) => res.blob())
    .then((blob) =>
      zip.loadAsync(blob).then((extracted) =>
        buildTree(
          Object.entries(extracted.files).map(([pathname, file]) => {
            const path = pathname.split("/");
            const isDir = file.dir;
            const name =
              (isDir ? path[path.length - 2] : path[path.length - 1]) ||
              file.name;

            return {
              type: isDir ? "folder" : "document",
              path: path.slice(0, -1),
              name,
              mimeType: fileNameToMimeType(extMap, name),
            };
          }),
        ),
      ),
    )
    .then((tree) => {
      const hasRootDir = tree.length === 1 && tree[0].type === "folder";

      return hasRootDir
        ? tree[0]
        : ({
            type: "folder",
            name: "/",
            children: tree,
            mimeType: "",
          } as Node);
    });
}

export { extractZip };
